// /sales REST + WS (per-agent channels)
//  - POST /sales/start-sale-call            -> starts two transcriptions + agents
//  - POST /sales/require-immediate-help     -> triggers WhatToResponse soon
//  - WS channels:
//      /sales/ws/is-response-needed
//      /sales/ws/sales-stage
//      /sales/ws/speech-tone
//      /sales/ws/summarizer
//      /sales/ws/what-to-response

import { WSClient } from "./apiBase/wsClient.js";

export class SalesAPI {
  /** @param {import('../httpClient').HttpClient} http @param {{wsURL:string}} cfg */
  constructor(http, cfg) {
    this.http = http;
    this.wsURL = cfg.wsURL;
  }

  /**
   * Connect to feedback stream SSE
   * @param {string} callId
   * @returns {EventSource}
   */
  connectFeedbackStream(callId) {
    // Explicitly use the http baseURL to ensure we hit the local audio server
    // even if served from a different origin (e.g. production frontend)
    const url = `${this.http.baseURL}/sales/feedback-stream?call_id=${callId}`;
    return new EventSource(url);
  }

  /**
   * Connect to short summary stream SSE
   * @param {string} callId
   * @returns {EventSource}
   */
  connectSummaryStream(callId) {
    const url = `${this.http.baseURL}/sales/summary-stream?call_id=${callId}`;
    return new EventSource(url);
  }

  /**
   * Connect to WhatsApp followup stream SSE
   * @param {string} callId
   * @returns {EventSource}
   */
  connectWhatsappStream(callId) {
    const url = `${this.http.baseURL}/sales/whatsapp-stream?call_id=${callId}`;
    return new EventSource(url);
  }

  /**
   * Connect to after-call analysis stream SSE
   * @param {string} callId
   * @returns {EventSource}
   */
  connectAnalysisStream(callId) {
    const url = `${this.http.baseURL}/sales/analysis-stream?call_id=${callId}`;
    return new EventSource(url);
  }

  /**
   * Trigger on-demand after-call analysis.
   * @param {string} callId
   * @param {string} [transcript] - Optional full transcript override (for historical calls)
   * @param {string} [summary]    - Optional summary override (for historical calls)
   * @returns {Promise}
   */
  runAfterCallAnalysis(callId, transcript, summary) {
    return this.http.post("/sales/run-aftercall-analysis", {
      call_id: callId,
      transcript: transcript || "",
      summary: summary || "",
    });
  }

  /**
   * Body: { callId, userId?, salesmanMicId, clientMicId, centerType?, micMinDbFS? }
   * Returns { message: "sales call started" }
   */
  startSaleCall({ callId, userId, salesmanMicId, clientMicId, centerType, micMinDbFS }) {
    return this.http.post("/sales/start-sale-call", {
      callId,
      userId,
      salesmanMicId,
      clientMicId,
      centerType,
      micMinDbFS,
    });
  }

  /**
   * Body: {
   *   callId: string,
   *   userId?: string,
   *   salesmanMicId: string,
   *   clientPlaybackDeviceId: string,
   *   businessInfo: string,
   *   difficultyLevel: "easy" | "medium" | "hard",
   *   voice?: "Eve" | "Ara" | "Rex" | "Sal" | "Leo",
   *   centerType?: string,
   *   micMinDbFS?: number
   * }
   */
  startSimulatorCall({ callId, userId, salesmanMicId, clientPlaybackDeviceId, businessInfo, difficultyLevel, voice, centerType, micMinDbFS }) {
    return this.http.post("/sales/start-simulator-call", {
      callId,
      userId,
      salesmanMicId,
      clientPlaybackDeviceId,
      businessInfo,
      difficultyLevel,
      voice,
      centerType,
      micMinDbFS,
    });
  }

  stopSaleCall() {
    return this.http.post("/sales/stop-sale-call");
  }

  stopSimulatorCall() {
    return this.http.post("/sales/stop-simulator-call");
  }

  pauseSaleCall() {
    return this.http.post("/sales/pause-sale-call");
  }

  resumeSaleCall() {
    return this.http.post("/sales/resume-sale-call");
  }

  requireImmediateHelp() {
    return this.http.post("/sales/require-immediate-help");
  }

  sendFullTranscript(summary) {
    return this.http.post("/sales/send-full-transcript", summary ? { summary } : undefined);
  }

  generateWhatsAppMessages(summary, transcript) {
    return this.http.post("/sales/generate-whatsapp-messages", {
      summary: summary || "",
      transcript: transcript || ""
    });
  }

  generateShortSummary(transcript) {
    return this.http.post("/sales/generate-short-summary", {
      transcript: transcript || ""
    });
  }

  /**
   * Start the AI-automated checklist detection
   * Body: { callId: string, businessInfo: string, questions: Array<{id, index, text, category_id?, is_done}> }
   */
  startChecklistDetector({ callId, businessInfo, questions }) {
    return this.http.post("/sales/start-checklist-detector", {
      callId,
      businessInfo: businessInfo || "",
      questions: questions || []
    });
  }

  /**
   * Ask AI guidance for a specific checklist question
   * Body: { questionId: string, questionText: string }
   * Returns { queued: true }
   */
  askChecklistQuestion({ questionId, questionText }) {
    return this.http.post("/sales/ask-checklist-question", {
      questionId,
      questionText
    });
  }

  /**
   * Dig deeper into a checklist question that was already asked
   * Body: { questionId: string, questionText: string }
   * Returns { queued: true }
   */
  digDeeper({ questionId, questionText }) {
    return this.http.post("/sales/dig-deeper", {
      questionId,
      questionText
    });
  }

  /**
   * Run a named onboarding tour agent without an active call
   * Body: { agentName: string, questionText: string, businessInfo: string }
   * Returns { result: string }
   */
  runTourAgent({ agentName, questionText, businessInfo }) {
    return this.http.post("/sales/tour/run", {
      agentName,
      questionText: questionText || "",
      businessInfo: businessInfo || "",
    });
  }

  /**
   * Handle an objection by sending it to the audio server for processing
   * Body: { resistence_name: string, resistence_solutions: string, current_summary: any }
   */
  handleObjection({ resistence_name, resistence_solutions, current_summary }) {
    return this.http.post("/sales/handle-objection", {
      resistence_name,
      resistence_solutions,
      current_summary
    });
  }

  // ---- WebSockets (one per agent channel) ----

  /**
   * @param {{onMessage?:(m:any)=>void,onOpen?:()=>void,onClose?:(e:CloseEvent)=>void,onError?:(e:Event)=>void}} [opts]
   */
  wsIsResponseNeeded(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/is-response-needed`, opts);
  }

  wsSalesStage(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/sales-stage`, opts);
  }

  wsSpeechTone(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/speech-tone`, opts);
  }

  wsSummarizer(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/summarizer`, opts);
  }

  wsWhatToResponse(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/what-to-response`, opts);
  }

  /**
   * Connect to checklist detector WebSocket for answered question events
   * @param {{onMessage?:(m:any)=>void,onOpen?:()=>void,onClose?:(e:CloseEvent)=>void,onError?:(e:Event)=>void}} [opts]
   */
  wsChecklist(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/checklist`, opts);
  }

  /**
   * Upload a call plan file to generate checklist questions
   * @param {FormData} formData 
   */
  uploadCallPlan(formData) {
    return this.http.post("/sales/upload-call-plan", formData);
  }

  /**
   * Connect to call plan to checklist WebSocket
   * @param {{onMessage?:(m:any)=>void,onOpen?:()=>void,onClose?:(e:CloseEvent)=>void,onError?:(e:Event)=>void}} [opts]
   */
  wsCallPlanToChecklist(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/call-plan-to-checklist`, opts);
  }

  /**
   * Connect to business info to checklist WebSocket
   * @param {{onMessage?:(m:any)=>void,onOpen?:()=>void,onClose?:(e:CloseEvent)=>void,onError?:(e:Event)=>void}} [opts]
   */
  wsBusinessInfoToChecklist(opts) {
    return new WSClient(`${this.wsURL}/sales/ws/business-info-to-checklist`, opts);
  }
}
